/**
 * Helper functions for Modern to resolve the directives and replace with DOM templates
 */
import { beforeText, has, remove, wrapAround } from "./common-utils";

/**
 * Get a string and turn it into template literal
 * @param {string} tpl
 * @returns {function}
 *
 * x = parseLit(string)
 * x(state) // to update
 */
const parseLit = tpl => state => new Function(`return \`${tpl}\``).call(state);

/**
 * :if directive
 * @param {HTMLElement} el
 * @param {string} value Directive value ex. :if="this.props.messageText"
 * @param {string} directive
 * @returns {void}
 */
function _if(el, value, directive) {
    remove(el, directive);
    beforeText(el, `\${${value} ? `);
    const rElse = el.nextElementSibling;

    if (rElse && has(rElse, "else")) {
        wrapAround(el, "`", "`");
        remove(rElse, "else");
        wrapAround(rElse, ":`", "`}");
    } else {
        wrapAround(el, "`", "`:``}");
    }
}

/**
 * :for directive
 * @todo: add for else => :else for for, it's an if condition that test the length,
 * @param {HTMLElement} el
 * @param {string} value Directive value ex. :for="item in this.messageList"
 * @param {string} directive
 * @returns {void}
 */
function _for(el, value, directive) {
    const groups = /(.*)\s+(in)\s+(.*)$/.exec(value);

    if (groups.length === 4) {
        const sel = groups[1].replace("(", "").replace(")", "");
        const query = groups[3];
        wrapAround(el, `\${${query}.map(function(${sel}) { return \``, "`}.bind(this)).join('')}");
        remove(el, directive);
    }
}

/**
 * :class directive
 * <div :class="clsName:condition; clsName2: condition2"></div>
 * <div :class="hide: this.item > 5; show-my-ownclass: x === y"></div>
 * @param {HTMLElement} el
 * @param {string} value Directive value ex. :class="hide: this.item > 5; show-my-ownclass: x === y"
 * @param {string} directive
 * @returns {void}
 */
function _class(el, value, directive) {
    const klass = value
        .split(";")
        .map(v => v.split(":", 2).map(e => e.trim()))
        .map(v => `\${${v[1]} ? '${v[0]}': ''}`)
        .join(" ");
    const classList = el.getAttribute("class") || "" + ` ${klass}`;
    el.setAttribute("class", classList);
    remove(el, directive);
}

/**
 * :key directive
 * <div :key="{index}"></div> will be change to <div ref-key="{index}">
 * to make sure iteration is rendered properly
 * @param {HTMLElement} el
 * @param {string} value Directive Value ex. :key="index"
 * @param {string} directive
 * @returns {void}
 */
function _key(el, value, directive) {
    el.setAttribute("ref-key", value);
    remove(el, directive);
}

/**
 * :style directive
 * @param {HTMLElement} el
 * @param {string} value Directive value :style="this.nameStyle"
 * @param {string} directive
 * @private
 */
function _style(el, value, directive) {
    const oStyle = el.getAttribute("style") || "";
    const style = `\${function() { return this.__$objToStyleMap(${value});}.call(this)}`;
    el.setAttribute("style", (oStyle ? `${oStyle}; ` : "") + style);
    remove(el, directive);
}

export {
    _if,
    _for,
    _class,
    _key,
    _style,
    parseLit,
};
